import { DiaSemana, ICreditTerm } from '../types';

/**
 * Traduce una condición de crédito a español legible.
 *
 * Es la pieza central de la UX de esta feature: una condición NUNCA se muestra
 * como parámetros crudos (`vencRegla: DIA_DEL_MES, offset: 1`). Quien mira la
 * pantalla —Comercial, Cobranzas, un vendedor facturando— tiene que entender
 * la condición sin saber cómo está modelada.
 *
 * Ejemplos de salida:
 *   "30 días desde la emisión de la factura"
 *   "Corte quincenal (15 y último día) · vence 10 días después del corte"
 *   "Corte mensual · vence el 15 del mes siguiente"
 *   "Vence el tercer miércoles del mes siguiente"
 */

const DIA_LABEL: Record<DiaSemana, string> = {
	DOMINGO: 'domingo',
	LUNES: 'lunes',
	MARTES: 'martes',
	MIERCOLES: 'miércoles',
	JUEVES: 'jueves',
	VIERNES: 'viernes',
	SABADO: 'sábado',
};

const ORDINAL: Record<string, string> = {
	'1': 'primer',
	'2': 'segundo',
	'3': 'tercer',
	'4': 'cuarto',
	ULTIMA: 'último',
};

/** offset 0 → "del mes" · 1 → "del mes siguiente" · n → "a N meses". */
function mesLabel(offset: number): string {
	if (!offset) return 'del mes';
	if (offset === 1) return 'del mes siguiente';
	return `a ${offset} meses`;
}

/** [15, 31] → "15 y último día" · [10, 20] → "10 y 20" · [15] → "15". */
function diasMesLabel(dias: number[]): string {
	const partes = dias.map((d) => (d >= 31 ? 'último día' : String(d)));
	if (partes.length === 1) return partes[0];
	return `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
}

/** Desde qué fecha se cuenta, en la forma que encaja después de "N días". */
function baseLabel(term: ICreditTerm): string {
	switch (term.fechaBase) {
		case 'CORTE':
			return 'después del corte';
		case 'EMISION_SERVICIO':
			return 'desde la emisión del servicio';
		default:
			return 'desde la emisión de la factura';
	}
}

/** El eje del corte. Devuelve null cuando no hay corte (se factura al emitir). */
export function describeCorte(term: ICreditTerm): string | null {
	switch (term.corteTipo) {
		case 'SEMANAL':
			return term.corteDiaSemana ? `Corte semanal (${DIA_LABEL[term.corteDiaSemana]})` : 'Corte semanal';
		case 'QUINCENAL':
			return `Corte quincenal (${diasMesLabel(term.corteDiasMes?.length ? term.corteDiasMes : [15, 31])})`;
		case 'MENSUAL':
			return 'Corte mensual (último día)';
		case 'PERSONALIZADO':
			return 'Corte personalizado';
		default:
			return null;
	}
}

/** El eje del vencimiento. */
export function describeVencimiento(term: ICreditTerm): string {
	if (!term.vencRegla) return 'Vencimiento a definir a mano';

	switch (term.vencRegla) {
		case 'DIAS_FIJOS': {
			const dias = term.vencDiasCorridos ?? 0;
			if (dias === 0) return 'Vence el mismo día (contado)';
			return `${dias} ${dias === 1 ? 'día' : 'días'} ${baseLabel(term)}`;
		}
		case 'ULTIMO_DIA_MES':
			return `Vence el último día ${mesLabel(term.vencOffsetMeses)}`;
		case 'DIA_DEL_MES':
			return `Vence el ${term.vencDiaDelMes} ${mesLabel(term.vencOffsetMeses)}`;
		case 'ENESIMO_DIA_SEMANA': {
			const ordinal = ORDINAL[term.vencSemana ?? '1'] ?? 'primer';
			const dia = term.vencDiaSemana ? DIA_LABEL[term.vencDiaSemana] : 'día';
			return `Vence el ${ordinal} ${dia} ${mesLabel(term.vencOffsetMeses)}`;
		}
		default:
			return 'Vencimiento a definir a mano';
	}
}

/**
 * La condición completa en una línea. Es lo que se muestra en el chip del
 * drawer, en la ficha del cliente y en el listado de gestión.
 */
export function describeCreditTerm(term: ICreditTerm): string {
	if (term.corteTipo === 'PERSONALIZADO') {
		return 'Condición personalizada — la define una persona';
	}
	const corte = describeCorte(term);
	const venc = describeVencimiento(term);
	return corte ? `${corte} · ${venc.toLowerCase()}` : venc;
}

/**
 * Los modificadores, como líneas secundarias. Se muestran aparte porque son
 * excepciones y conviene que se lean como tales, no mezcladas con la regla.
 */
export function describeModificadores(term: ICreditTerm): string[] {
	const out: string[] = [];
	if (term.ajusteFinDeSemana === 'ANTERIOR') {
		out.push('Si cae fin de semana, se adelanta al viernes');
	}
	if (term.ajusteFinDeSemana === 'POSTERIOR') {
		out.push('Si cae fin de semana, se posterga al lunes');
	}
	if (term.topeDiasAntesViaje != null) {
		out.push(`Nunca vence después de ${term.topeDiasAntesViaje} días antes del viaje`);
	}
	return out;
}

/** Etiqueta corta para chips donde no entra la descripción completa. */
export function shortTermLabel(term: ICreditTerm): string {
	if (term.vencRegla === 'DIAS_FIJOS' && term.vencDiasCorridos != null) {
		return `${term.vencDiasCorridos} días`;
	}
	return term.name;
}

/** 'YYYY-MM-DD' → '15/01/2026' sin corrimiento de zona horaria. */
export function formatFecha(iso: string | null | undefined): string {
	if (!iso) return '—';
	// Solo formatea si es realmente YYYY-MM-DD. Si llega otra cosa, se muestra
	// tal cual: mejor un dato crudo que un "undefined/undefined" en pantalla.
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
	if (!match) return iso;
	const [, y, m, d] = match;
	return `${d}/${m}/${y}`;
}
